import {
  Table,
  Tbody,
  Thead,
  Th,
  Tr,
  Td,
  TableContainer,
  VStack,
  Text,
} from '@chakra-ui/react';
import React from 'react';

import {
  utils as commonUtils,
  finleyAssertUnreachable,
} from '@finley-technologies/common';
import { Funding } from '@finley-technologies/finley-types';

import {
  TrWithPanel,
  TrWithPanelRow,
  TrWithPanelPanel,
} from '../AccordionTableV2';
import { percentageToPercentageLabel } from '~/packages/common-frontend/utils';

type PricingOptionType = Funding.LoanMechanics['pricingOptions'][number];

type FixedRateOption = Extract<PricingOptionType, { type: 'fixed' }>;

type BenchmarkRateOption = Extract<PricingOptionType, { type: 'benchmark' }>;

type PricingOptionProps = {
  pricingOption: PricingOptionType;
};

const PricingOptionFixedType = ({
  pricingOption,
}: {
  pricingOption: FixedRateOption;
}) => {
  return (
    <TableContainer w="100%">
      <Table borderWidth="1px">
        <Thead>
          <Tr>
            <Th>Name</Th>
            <Th>Value</Th>
          </Tr>
        </Thead>
      </Table>
      <Tbody>
        <Tr>
          <Td>Type</Td>
          <Td>{commonUtils.toTitleCase(pricingOption.type)}</Td>
        </Tr>
        <Tr>
          <Td>Value</Td>
          {/* @jendevelops TODO: how big should the scale be? */}
          <Td>{percentageToPercentageLabel(pricingOption.value * 100, 4)}</Td>
        </Tr>
      </Tbody>
    </TableContainer>
  );
};

const RateAdjustment = ({
  rateAdjustment,
}: {
  rateAdjustment: BenchmarkRateOption['adjustments'][number];
}) => {
  switch (rateAdjustment.type) {
    case 'floor':
    case 'ceiling':
      return (
        <Tr>
          <Td>{commonUtils.toTitleCase(rateAdjustment.type)}</Td>
          <Td>{percentageToPercentageLabel(rateAdjustment.value * 100, 4)}</Td>
          <Td>-</Td>
        </Tr>
      );
    case 'adjustment':
      return (
        <Tr>
          <Td>Rate Adjustment</Td>
          <Td>{percentageToPercentageLabel(rateAdjustment.value * 100, 10)}</Td>
          <Td>
            <VStack alignItems="flex-start">
              {rateAdjustment.creditAdjustmentSpread && (
                <Text>Credit adjustment spread</Text>
              )}
            </VStack>
          </Td>
        </Tr>
      );
    case 'rounding':
      return (
        <Tr>
          <Td>Rounding</Td>
          <Td>{commonUtils.toTitleCase(rateAdjustment.mode)}</Td>
          <Td>
            <Text>{`Decimal Places: ${String(
              rateAdjustment.decimalPlaces,
            )}`}</Text>
          </Td>
        </Tr>
      );
    default:
      return finleyAssertUnreachable(rateAdjustment);
  }
};

const Change = ({ change }: { change: BenchmarkRateOption['change'] }) => {
  switch (change.type) {
    case 'daily':
      return (
        <Tr>
          <Td>Change</Td>
          <Td>Daily</Td>
        </Tr>
      );
    case 'recurring':
      return (
        <Tr>
          <Td>Change</Td>
          <Td>Recurring</Td>
          {/* @jendevelops TODO: display recurring config */}
        </Tr>
      );
    default:
      return finleyAssertUnreachable(change);
  }
};

const Determination = ({
  determination,
}: {
  determination: BenchmarkRateOption['determination'];
}) => {
  return (
    <Tr>
      <Td>{commonUtils.toTitleCase(determination.type)}</Td>
      <Td>{`${determination.lookback.days} business day${
        Math.abs(determination.lookback.days) > 0 ? 's' : ''
      }`}</Td>
      {/* @jendevelops TODO add in business days list */}
    </Tr>
  );
};

const PricingOptionBenchmarkType = ({
  pricingOption,
}: {
  pricingOption: BenchmarkRateOption;
}) => {
  return (
    <TableContainer w="100%">
      <Table borderWidth="1px">
        <Thead>
          <Tr>
            <Th>Name</Th>
            <Th>Value</Th>
            <Th width="10%" />
          </Tr>
        </Thead>
        <Tbody>
          <Tr>
            <Td>Type</Td>
            <Td>{commonUtils.toTitleCase(pricingOption.type)}</Td>
            <Td />
          </Tr>
          <Tr>
            <Td>Benchmark</Td>
            <Td>{pricingOption.benchmark}</Td>
            <Td />
          </Tr>
          <TrWithPanel>
            {() => (
              <>
                <TrWithPanelRow>
                  <Td>Adjustments</Td>
                  <Td />
                </TrWithPanelRow>
                <TrWithPanelPanel>
                  <Table borderWidth="1px">
                    <Thead>
                      <Tr>
                        <Th>Type</Th>
                        <Th>Value</Th>
                        <Th>Options</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {pricingOption.adjustments.map((rateAdjustment) => (
                        <RateAdjustment
                          // @jendevelops might need more specific key
                          key={rateAdjustment.type}
                          rateAdjustment={rateAdjustment}
                        />
                      ))}
                    </Tbody>
                  </Table>
                </TrWithPanelPanel>
              </>
            )}
          </TrWithPanel>
          <TrWithPanel>
            {() => (
              <>
                <TrWithPanelRow>
                  <Td>Change</Td>
                  <Td />
                </TrWithPanelRow>
                <TrWithPanelPanel>
                  <Table borderWidth="1px">
                    <Thead>
                      <Tr>
                        <Th>Type</Th>
                        <Th>Value</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      <Change change={pricingOption.change} />
                    </Tbody>
                  </Table>
                </TrWithPanelPanel>
              </>
            )}
          </TrWithPanel>
          <TrWithPanel>
            {() => (
              <>
                <TrWithPanelRow>
                  <Td>Determination</Td>
                  <Td />
                </TrWithPanelRow>
                <TrWithPanelPanel>
                  <Table borderWidth="1px">
                    <Thead>
                      <Tr>
                        <Th>Type</Th>
                        <Th>Value</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      <Determination
                        determination={pricingOption.determination}
                      />
                    </Tbody>
                  </Table>
                </TrWithPanelPanel>
              </>
            )}
          </TrWithPanel>
        </Tbody>
      </Table>
    </TableContainer>
  );
};

export const PricingOption = ({ pricingOption }: PricingOptionProps) => {
  return (
    <>
      {(() => {
        switch (pricingOption.type) {
          case 'fixed':
            return <PricingOptionFixedType pricingOption={pricingOption} />;
          case 'benchmark':
            return <PricingOptionBenchmarkType pricingOption={pricingOption} />;
          default:
            return finleyAssertUnreachable(pricingOption);
        }
      })()}
    </>
  );
};
